import React, { useEffect } from 'react';
import { AddressToDistrict } from '../../districts/addrtodistrict';
import { useFormContext } from './FormContext';

// Basic text input field
export const Name = ({ required = false, placeholder = 'Enter your name' }) => {
  const { registerField, updateField, getFieldValue } = useFormContext();

  useEffect(() => {
    registerField({
      id: 'name',
      type: 'text',
      required,
    });
  }, [required]); // Remove registerField from dependencies

  return (
    <div className="w-full">
      <label className="block text-sm font-semibold mb-1">
        Your Name {required && <span className="text-red-500">* Required</span>}
      </label>
      <input
        type="text"
        value={getFieldValue('name')}
        onChange={(e) => updateField('name', e.target.value)}
        placeholder={placeholder}
        className="w-full p-2 border rounded-md text-sm"
        required={required}
      />
    </div>
  );
};

// Age number input
export const Age = ({ label = 'Age', placeholder = 'Your age', min = 13, max = 120 }) => {
  const { registerField, updateField, getFieldValue } = useFormContext();

  useEffect(() => {
    registerField({
      id: 'age',
      type: 'number',
      formatter: (value) => (value ? `I am ${value} years old` : null),
    });
  }, []); // Empty deps array

  return (
    <div className="w-full">
      <label className="block text-sm font-semibold mb-1">{label}</label>
      <input
        type="number"
        value={getFieldValue('age')}
        onChange={(e) => updateField('age', e.target.value)}
        placeholder={placeholder}
        className="w-full p-2 border rounded-md text-sm"
        min={min}
        max={max}
      />
    </div>
  );
};

// Years in SF number input
export const YearsInSF = ({
  label = 'Years in SF',
  placeholder = 'Years living in SF',
  min = 0,
  max = 100,
  formatter = null, // Allow custom formatter
}) => {
  const { registerField, updateField, getFieldValue } = useFormContext();

  const defaultFormatter = (value) => {
    if (!value) return null;
    if (value === '1') return 'I have lived in San Francisco for 1 year';
    if (value === '0') return 'I am new to San Francisco';
    return `I have lived in San Francisco for ${value} years`;
  };

  useEffect(() => {
    registerField({
      id: 'yearsInSF',
      type: 'number',
      formatter: formatter || defaultFormatter,
    });
  }, [formatter]);

  return (
    <div className="w-full">
      <label className="block text-sm font-semibold mb-1">{label}</label>
      <input
        type="number"
        value={getFieldValue('yearsInSF')}
        onChange={(e) => updateField('yearsInSF', e.target.value)}
        placeholder={placeholder}
        className="w-full p-2 border rounded-md text-sm"
        min={min}
        max={max}
      />
    </div>
  );
};

// Address component wrapper
export const AddressToDistrictField = ({ label = 'Your Address' }) => {
  const { registerField, updateField, getFieldValue } = useFormContext();

  useEffect(() => {
    registerField({
      id: 'address',
      type: 'address',
      formatter: (district, supervisorName) =>
        district && supervisorName ? `I live in District ${district}` : null,
    });
  }, []);

  const handleDistrictChanged = (info) => {
    if (info?.supervisor) {
      updateField('district', info.supervisor.districtNum);
      updateField('supervisorName', `${info.supervisor.firstName} ${info.supervisor.lastName}`);
      updateField('address', info.chosenAddress);
      updateField('zipCode', info.zipCode);
    }
  };

  const district = getFieldValue('district');
  const supervisorName = getFieldValue('supervisorName');
  const zipCode = getFieldValue('zipCode');

  return (
    <div className="w-full">
      <label className="block text-sm font-semibold mb-1">{label}</label>
      <AddressToDistrict onDistrictChanged={handleDistrictChanged} onAddressSelected={() => {}} />
      {district && (
        <p className="mt-2 text-sm text-gray-600">
          {zipCode && `${zipCode} - `}District {district} - Supervisor {supervisorName}
        </p>
      )}
    </div>
  );
};

// Business name text input
export const BusinessName = ({
  required = false,
  placeholder = 'Your business name',
  label = 'Business Name',
}) => {
  const { registerField, updateField, getFieldValue } = useFormContext();

  useEffect(() => {
    registerField({
      id: 'businessName',
      type: 'text',
      required,
      formatter: (value) => (value ? `I own ${value}` : null),
    });
  }, [required]);

  return (
    <div className="w-full">
      <label className="block text-sm font-semibold mb-1">
        {label} {required && <span className="text-red-500">* Required</span>}
      </label>
      <input
        type="text"
        value={getFieldValue('businessName')}
        onChange={(e) => updateField('businessName', e.target.value)}
        placeholder={placeholder}
        className="w-full p-2 border rounded-md text-sm"
        required={required}
      />
    </div>
  );
};

// Business type text input
export const BusinessType = ({
  required = false,
  placeholder = 'e.g., Restaurant, Tech startup, Retail store',
  label = 'Business Type',
}) => {
  const { registerField, updateField, getFieldValue } = useFormContext();

  useEffect(() => {
    registerField({
      id: 'businessType',
      type: 'text',
      required,
      formatter: (value) => (value ? `I run a ${value.toLowerCase()}` : null),
    });
  }, [required]);

  return (
    <div className="w-full">
      <label className="block text-sm font-semibold mb-1">
        {label} {required && <span className="text-red-500">* Required</span>}
      </label>
      <input
        type="text"
        value={getFieldValue('businessType')}
        onChange={(e) => updateField('businessType', e.target.value)}
        placeholder={placeholder}
        className="w-full p-2 border rounded-md text-sm"
        required={required}
      />
    </div>
  );
};

// Custom notes textarea
export const CustomNotes = ({
  label = 'Additional Context',
  placeholder = "Any personal story or specific concerns you'd like to include...",
  rows = 3,
}) => {
  const { registerField, updateField, getFieldValue } = useFormContext();

  useEffect(() => {
    registerField({
      id: 'customNotes',
      type: 'textarea',
      formatter: (value) => value || null,
    });
  }, []);

  return (
    <div>
      <label className="block text-sm font-semibold mb-1">{label}</label>
      <textarea
        value={getFieldValue('customNotes')}
        onChange={(e) => updateField('customNotes', e.target.value)}
        placeholder={placeholder}
        className="w-full p-2 border rounded-md text-sm"
        rows={rows}
      />
    </div>
  );
};

// Conditional field wrapper - shows children only when a condition is met
export const ConditionalField = ({ condition, value, children }) => {
  const { getFieldValue } = useFormContext();
  
  // Get the current value of the condition field
  const fieldValue = getFieldValue(condition);
  
  // For checkbox groups (arrays), check if the value is included
  if (Array.isArray(fieldValue)) {
    if (!fieldValue.includes(value)) {
      return null;
    }
  } 
  // For radio or single values, check direct equality
  else if (fieldValue !== value) {
    return null;
  }
  
  return <>{children}</>;
};

// Layout helpers
export const FormRow = ({ children, className = '' }) => (
  <div className={`flex flex-col sm:flex-row justify-center sm:space-x-4 w-full ${className}`}>
    {children}
  </div>
);

export const FormColumn = ({ children, className = '' }) => (
  <div className={`flex flex-col space-y-4 w-full ${className}`}>{children}</div>
);

export const FormSection = ({ children, className = '' }) => (
  <div className={`space-y-2 ${className}`}>
    {children}
  </div>
);